
/**
 * Store ID Manager for Development Environment
 * 
 * This utility manages store switching in development using localStorage.
 * It allows developers to easily switch between different stores for testing.
 */

export interface StoreInfo {
  id: string;
  name: string;
  description?: string;
}

// Predefined stores for development
export const AVAILABLE_STORES: StoreInfo[] = [
  { id: 'zpureair', name: 'ZPureAir (CA)', description: 'Canadian wholesale storefront' },
  { id: 'zlab-uk', name: 'Z-Lab UK', description: 'UK Store for Z-Lab' },
  { id: 'zlab-us', name: 'Z-Lab US', description: 'US Store for Z-Lab' },
  { id: 'demo-store', name: 'Demo Store', description: 'Demo store for testing' },
  { id: 'test-store', name: 'Test Store', description: 'Test store for development' },
];

const STORE_ID_KEY = 'dev_store_id';
const DEFAULT_STORE_ID = 'zlab-uk';

/**
 * Check if we're in development environment
 */
export const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('dev');
};

/**
 * Get the current store ID from localStorage or default
 */
export const getCurrentStoreId = (): string => {
  if (!isDevelopment()) return DEFAULT_STORE_ID;
  
  if (typeof window === 'undefined') return DEFAULT_STORE_ID;
  
  const stored = localStorage.getItem(STORE_ID_KEY);
  return stored || DEFAULT_STORE_ID;
};

/**
 * Set the store ID in localStorage
 */
export const setCurrentStoreId = (storeId: string): void => {
  if (!isDevelopment()) return;
  
  if (typeof window === 'undefined') return;
  
  // Validate that the store ID exists in our predefined list
  const storeExists = AVAILABLE_STORES.some(store => store.id === storeId);
  if (!storeExists) {
    console.warn(`Store ID "${storeId}" not found in available stores. Adding as custom store.`);
  }
  
  localStorage.setItem(STORE_ID_KEY, storeId);
  
  // Trigger a custom event for listeners
  window.dispatchEvent(new CustomEvent('storeIdChanged', { 
    detail: { storeId, storeName: getStoreInfo(storeId)?.name || storeId }
  }));
};

/**
 * Get store information by ID
 */
export const getStoreInfo = (storeId: string): StoreInfo | undefined => {
  return AVAILABLE_STORES.find(store => store.id === storeId);
};

/**
 * Get current store information
 */
export const getCurrentStoreInfo = (): StoreInfo => {
  const storeId = getCurrentStoreId();
  const storeInfo = getStoreInfo(storeId);
  
  return storeInfo || { 
    id: storeId, 
    name: storeId,
    description: 'Custom store' 
  };
};

/**
 * Clear store ID from localStorage (reset to default)
 */
export const clearStoreId = (): void => {
  if (!isDevelopment()) return;
  
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem(STORE_ID_KEY);
  
  // Trigger event for reset
  window.dispatchEvent(new CustomEvent('storeIdChanged', { 
    detail: { storeId: DEFAULT_STORE_ID, storeName: getStoreInfo(DEFAULT_STORE_ID)?.name || DEFAULT_STORE_ID }
  }));
};

/**
 * Add store ID to URL if in development
 */
export const addStoreIdToUrl = (url: string): string => {
  if (!isDevelopment()) return url;
  
  const storeId = getCurrentStoreId();
  if (!storeId) return url;
  
  // Check if URL already has storeId parameter
  if (url.includes('storeId=')) return url;
  
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}storeId=${storeId}`;
};

/**
 * Hook for listening to store ID changes
 */
export const onStoreIdChange = (callback: (storeId: string, storeName: string) => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  
  const handler = (event: CustomEvent) => {
    callback(event.detail.storeId, event.detail.storeName);
  };
  
  window.addEventListener('storeIdChanged', handler as EventListener);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('storeIdChanged', handler as EventListener);
  };
};
